// File: /pages/Positions.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import Link from 'next/link';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
//import styles from './Positions.module.css'; // Optional: additional CSS for this page

// Helper component to render a single Position using a tabbed layout.
const PositionContentDisplay = ({ position }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  // --- Overview Tab ---
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p><strong>Status:</strong> {position.status || 'N/A'}</p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {position.submission_date
              ? new Date(position.submission_date).toLocaleDateString()
              : 'N/A'}
          </p>
          {position.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(position.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {position.created_at
              ? new Date(position.created_at).toLocaleDateString()
              : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {position.updated_at
              ? new Date(position.updated_at).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
        {/* Right Column: Perspective Carousel and DMCA Link */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={position} />
          <div className={displayStyles.dmcaLinkContainer}>
            <Link
              href={`/DMCA?contentHash=${position.content_id}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {position.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={position.content_id} />
    </div>
  );

  // --- Classification Tab ---
  const classificationTabContent = (
    <ClassificationDisplay content={position} />
  );

  // --- Media Tab ---
  const galleryImages = Array.isArray(position.gallery_images)
    ? position.gallery_images
    : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={position} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={position} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

const PositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch parent category mapping.
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch all content items and then filter for type "Position".
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await fetch('/api/content/content');
        if (!res.ok) throw new Error('Failed to fetch positions');
        const data = await res.json();
        let items = Array.isArray(data.content) ? data.content : [];
        // Filter items by type "Position" (case-insensitive)
        items = items.filter(
          (item) =>
            item.type && item.type.trim().toLowerCase() === 'position'
        );
        setPositions(items);
      } catch (err) {
        console.error('Error fetching positions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPositions();
  }, []);

  if (loading) return <p>Loading positions...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group positions by parent_category_id.
  const groups = {};
  positions.forEach((position) => {
    let parentCategories = [];
    if (position.parent_category_id) {
      try {
        parentCategories = JSON.parse(position.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        parentCategories = position.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(position);
    } else {
      parentCategories.forEach((cat) => {
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(position);
      });
    }
  });

  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((position) => ({
        header: position.name,
        content: <PositionContentDisplay position={position} />,
      })),
    };
  });

  return (
    <section id="positions" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Positions - Our Core Focus:</h1>
        <h2>
          Discover the various positions that are crucial to shuffle dance moves. Click on an accordion header to view the position details.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No positions found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Position" />
      </div>
    </section>
  );
};

export default PositionsPage;
